/**
 * Image preloader.
 *
 * Warms the browser cache for Academy media (track covers, course covers,
 * lesson posters) and — crucially — keeps a live reference to every decoded
 * Image object in a module-level store. Holding the reference prevents the
 * browser from evicting the decoded bitmap, so when an <img> remounts after a
 * page transition it renders instantly from memory instead of popping in.
 */

// Keep decoded images alive for the whole session (module scope = not GC'd).
const imageStore = new Map();

const preloadImage = (url) => {
  if (!url || imageStore.has(url)) return;

  const img = new Image();
  img.decoding = 'async';
  img.src = url;

  // Store immediately so concurrent calls don't double-load. Decoding (when
  // supported) keeps the bitmap ready so the first paint after a remount is
  // free of flicker.
  imageStore.set(url, img);
  if (typeof img.decode === 'function') {
    img.decode().catch(() => {
      /* decode can reject for cross-origin/not-yet-loaded — the cache warm-up
         via src is still in flight and good enough. */
    });
  }
};

/**
 * Preload a list of image URLs. Safe to call repeatedly: already-warmed URLs
 * are skipped.
 */
export const preloadImages = (urls = []) => {
  urls.forEach(preloadImage);
};

/**
 * Collect every cover/poster URL out of the live topics tree.
 */
export const collectAcademyImageUrls = (topics = []) => {
  const urls = [];
  topics.forEach((topic) => {
    if (topic.image) urls.push(topic.image);
    (topic.courses || []).forEach((course) => {
      if (course.cover) urls.push(course.cover);
      (course.lessons || []).forEach((lesson) => {
        if (lesson.preview) urls.push(lesson.preview);
      });
    });
  });
  return urls;
};
